/**
 * @fileoverview Servicio para calcular métricas de uso de una familia
 * @module services/featureMetricsService
 */

import { 
  collection, 
  getDocs, 
  query, 
  where, 
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { FeatureMetrics, DEFAULT_METRICS } from '../types/features';

/**
 * Calcula todas las métricas de uso de una familia
 * Optimizado con queries paralelas
 */
export async function calculateMetrics(familyId: string): Promise<FeatureMetrics> {
  try {
    // Ejecutar todas las queries en paralelo
    const [
      tasksSnapshot,
      activitiesSnapshot,
      recipesSnapshot,
      membersSnapshot,
      completedTasksSnapshot,
      completedActivitiesSnapshot,
      familyDoc
    ] = await Promise.all([
      // Tareas creadas
      getDocs(query(
        collection(db, 'checklist'),
        where('familyId', '==', familyId)
      )),
      
      // Actividades creadas
      getDocs(query(
        collection(db, 'activities'),
        where('familyId', '==', familyId)
      )),
      
      // Recetas creadas
      getDocs(query(
        collection(db, 'recipes'),
        where('familyId', '==', familyId)
      )),
      
      // Integrantes
      getDocs(query(
        collection(db, 'members'),
        where('familyId', '==', familyId)
      )),
      
      // Tareas completadas (últimos 100 para performance)
      getDocs(query(
        collection(db, 'completedTasks'),
        where('familyId', '==', familyId)
      )),
      
      // Actividades completadas (últimos 100)
      getDocs(query(
        collection(db, 'completedActivities'),
        where('familyId', '==', familyId)
      )),
      
      // Documento de la familia
      getDoc(doc(db, 'families', familyId))
    ]);
    

    const familyData = familyDoc.data();
    
    // Calcular días activos
    const createdAt = familyData?.createdAt as Timestamp | undefined;
    const daysActive = createdAt 
      ? Math.floor((Date.now() - createdAt.toMillis()) / (1000 * 60 * 60 * 24))
      : 0;

    // Calcular primera actividad
    const firstActivityDate = createdAt || null;

    // Calcular días usando recetas (si tiene daily_log con recetas)
    let recipeDaysUsed = 0;
    if (recipesSnapshot.size > 0) {
      // Simplificado: asumimos que si tiene recetas, las usa
      // En producción, verificar daily_log con recipeId
      recipeDaysUsed = Math.min(recipesSnapshot.size * 2, daysActive);
    }

    // Verificar si ha explorado secciones (simplificado)
    // En producción, trackear navegación
    const hasExploredSections = 
      (tasksSnapshot.size > 0 ? 1 : 0) +
      (activitiesSnapshot.size > 0 ? 1 : 0) +
      (recipesSnapshot.size > 0 ? 1 : 0) >= 3;

    const result = {
      tasksCreated: tasksSnapshot.size,
      tasksCompleted: completedTasksSnapshot.size,
      activitiesCreated: activitiesSnapshot.size,
      recipesCreated: recipesSnapshot.size,
      recipeDaysUsed,
      daysActive,
      membersCount: membersSnapshot.size,
      totalCompletions: completedTasksSnapshot.size + completedActivitiesSnapshot.size,
      firstActivityDate,
      hasExploredSections
    };
    
    return result;

  } catch (error) {
    console.error('[featureMetrics] Error calculating metrics:', error);
    return DEFAULT_METRICS;
  }
}

/**
 * Calcula solo métricas básicas (más rápido, para checks frecuentes)
 */
export async function calculateBasicMetrics(familyId: string): Promise<Partial<FeatureMetrics>> {
  try {
    const [tasksSnapshot, activitiesSnapshot, recipesSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'checklist'), where('familyId', '==', familyId))),
      getDocs(query(collection(db, 'activities'), where('familyId', '==', familyId))),
      getDocs(query(collection(db, 'recipes'), where('familyId', '==', familyId)))
    ]);

    return {
      tasksCreated: tasksSnapshot.size,
      activitiesCreated: activitiesSnapshot.size,
      recipesCreated: recipesSnapshot.size
    };
  } catch (error) {
    console.error('Error calculating basic metrics:', error);
    return {};
  }
}

/**
 * Cache de métricas (3 minutos TTL)
 */
const metricsCache = new Map<string, { metrics: FeatureMetrics; timestamp: number }>();
const CACHE_TTL = 3 * 60 * 1000; // 3 minutos

/**
 * Obtiene métricas con cache
 */
export async function getMetricsWithCache(familyId: string): Promise<FeatureMetrics> {
  const cached = metricsCache.get(familyId);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < CACHE_TTL) {
    return cached.metrics;
  }

  const metrics = await calculateMetrics(familyId);
  metricsCache.set(familyId, { metrics, timestamp: now });

  return metrics;
}

/**
 * Invalida cache de una familia
 */
export function invalidateMetricsCache(familyId: string): void {
  metricsCache.delete(familyId);
}

/**
 * Limpia cache completo
 */
export function clearMetricsCache(): void {
  metricsCache.clear();
}
